import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { SEO } from "../components/SEO";
import { IMAGES, SERVICES } from "../data/content";

const stepsFor = ["Consultation & brief", "Design & documentation", "Transparent quotation", "Build & handover"];

export default function Services() {
  return (
    <div>
      <SEO
        title="Building Services | Custom Homes, Renovations & Extensions | Kotam Projects"
        description="Residential construction, renovations & extensions, and design & project management across Victoria and South Australia. Serving Melbourne, Craigieburn, Tarneit, Adelaide, Salisbury and more. Request a quote from Kotam Projects."
        path="/services"
      />
      <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-28 overflow-hidden bg-forest-dark text-white">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.homeExterior} alt="Premium custom home built by Kotam Projects in Victoria" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/80 to-forest-dark/50" />
        <div className="container-px relative">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="label text-gold">
            Our Services
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="mt-6 font-display font-light text-hero max-w-4xl">
            Built for every ambition.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-8 max-w-2xl text-lg sm:text-xl text-white/70 leading-relaxed">
            From bespoke custom homes to renovations, extensions, and full project management, we deliver
            end-to-end construction with uncompromising quality and a single point of accountability.
          </motion.p>
        </div>
      </section>

      {SERVICES.map((s, i) => {
        const reversed = i % 2 === 1;
        return (
          <section key={s.slug} className={`py-24 sm:py-32 lg:py-40 ${i % 2 === 1 ? "bg-concrete" : "bg-white"}`} data-testid={`service-${s.slug}`}>
            <div className="container-px grid gap-14 lg:grid-cols-12 lg:gap-20 items-center">
              <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                <Reveal>
                  <div className="relative overflow-hidden rounded-[24px]">
                    <img src={IMAGES[s.image]} alt={s.title} className="h-[380px] sm:h-[480px] w-full object-cover" loading="lazy" />
                    <span className="absolute top-6 left-6 flex h-14 w-14 items-center justify-center rounded-full bg-white/90 backdrop-blur font-display text-xl font-bold text-forest">
                      0{i + 1}
                    </span>
                  </div>
                </Reveal>
              </div>
              <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                <Reveal delay={0.1}>
                  <p className="label text-gold">{s.tagline}</p>
                  <h2 className="mt-5 font-display font-light text-h2 text-charcoal">{s.title}</h2>
                  <p className="mt-6 text-lg text-charcoal/60 leading-relaxed">{s.overview}</p>
                </Reveal>
                <Reveal delay={0.18}>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {s.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest">
                          <Check className="h-3 w-3 text-white" />
                        </span>
                        <span className="text-charcoal/70">{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.24}>
                  <div className="mt-8 flex flex-wrap items-center gap-2">
                    {stepsFor.map((st, idx) => (
                      <span key={st} className="inline-flex items-center gap-2 text-sm text-charcoal/50">
                        <span className="text-gold font-semibold">0{idx + 1}</span> {st}
                        {idx < stepsFor.length - 1 && <span className="text-border mx-1">/</span>}
                      </span>
                    ))}
                  </div>
                  <Link to="/contact" data-testid={`service-cta-${s.slug}`} className="mt-8 group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 font-semibold text-white transition-colors hover:bg-gold hover:text-forest-dark">
                    Enquire about {s.title}
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      <CTASection />
    </div>
  );
}
